import { RouterProvider } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  hashQueryKey,
} from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import router from "./routers";
import AppContextProvider from "./context/AppContext";
import { getScheme } from "./services/api";

import "./i18n";
import "react-toastify/dist/ReactToastify.css";
import "./styles/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Zenith requests carry the selected scheme (services/api.ts), so each
      // answer is cached under it. With none selected, as for Polestar and a
      // customer at home, keys hash exactly as react-query's own.
      queryKeyHashFn: queryKey => {
        const scheme = getScheme();
        return hashQueryKey(scheme ? [...queryKey, { scheme }] : queryKey);
      },
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      retry(failureCount, error: any) {
        const { status } = error?.response;
        if (status >= 400 && status < 500) return false;
        else if (failureCount < 2) return true;
        else return false;
      },
      useErrorBoundary: (error: any) => {
        const isAccessDenied = error.response?.status === 403;
        const isInternalServerError = error.response?.status >= 500;

        return isAccessDenied || isInternalServerError;
      },
    },
  },
});

export default function App() {
  // Note for local development had to remove strict mode for maps
  // ref: https://github.com/google-map-react/google-map-react/issues/1113
  return (
    <QueryClientProvider client={queryClient}>
      <AppContextProvider>
        <RouterProvider router={router} />
      </AppContextProvider>
      <ToastContainer autoClose={30000} hideProgressBar={true} />
    </QueryClientProvider>
  );
}
